import { create } from "zustand";
import { persist } from "zustand/middleware";

// Generations older than this are considered stale and should be cleared
const STALE_GENERATION_MS = 5 * 60 * 1000; // 5 minutes

export interface CaptionVariant {
  id: string;
  text: string;
  characterCount: number;
}

export interface CaptionGenerationResult {
  captions: CaptionVariant[];
  altText: string;
  generationId?: number;
}

export interface CaptionGenerationState {
  postId: string;
  jobId?: string;
  status: "generating" | "ready" | "error";
  result?: CaptionGenerationResult;
  error?: string;
  startedAt: number;
}

interface CaptionGenerationStore {
  generations: Record<string, CaptionGenerationState>;
  startGeneration: (postId: string, jobId: string) => void;
  completeGeneration: (postId: string, result: CaptionGenerationResult) => void;
  failGeneration: (postId: string, error: string) => void;
  clearGeneration: (postId: string) => void;
  clearStaleGenerations: () => void;
  getGenerationState: (postId: string) => CaptionGenerationState | null;
  getGenerationByJobId: (jobId: string) => CaptionGenerationState | null;
}

export const useCaptionGenerationStore = create<CaptionGenerationStore>()(
  persist(
    (set, get) => ({
      generations: {},

      startGeneration: (postId: string, jobId: string) => {
        // Clear any stale generations first
        get().clearStaleGenerations();
        set((state) => ({
          generations: {
            ...state.generations,
            [postId]: {
              postId,
              jobId,
              status: "generating",
              startedAt: Date.now(),
            },
          },
        }));
      },

      completeGeneration: (postId: string, result: CaptionGenerationResult) => {
        set((state) => ({
          generations: {
            ...state.generations,
            [postId]: {
              ...state.generations[postId],
              status: "ready",
              result,
            },
          },
        }));
      },

      failGeneration: (postId: string, error: string) => {
        set((state) => ({
          generations: {
            ...state.generations,
            [postId]: {
              ...state.generations[postId],
              status: "error",
              error,
            },
          },
        }));
      },

      clearGeneration: (postId: string) => {
        set((state) => {
          const { [postId]: _, ...rest } = state.generations;
          return { generations: rest };
        });
      },

      clearStaleGenerations: () => {
        const now = Date.now();
        set((state) => {
          const filtered: Record<string, CaptionGenerationState> = {};
          for (const [postId, gen] of Object.entries(state.generations)) {
            // Keep if: ready/error status, or generating but not stale
            const isStale = gen.status === "generating" && (now - gen.startedAt) > STALE_GENERATION_MS;
            if (!isStale) {
              filtered[postId] = gen;
            }
          }
          return { generations: filtered };
        });
      },

      getGenerationState: (postId: string) => {
        const gen = get().generations[postId];
        if (!gen) return null;

        // Auto-clear stale "generating" states
        if (gen.status === "generating" && (Date.now() - gen.startedAt) > STALE_GENERATION_MS) {
          // Clear this stale generation
          get().clearGeneration(postId);
          return null;
        }

        return gen;
      },

      getGenerationByJobId: (jobId: string) => {
        const generations = get().generations;
        return Object.values(generations).find((g) => g.jobId === jobId) || null;
      },
    }),
    {
      name: "caption-generation-store",
      onRehydrateStorage: () => (state) => {
        // Clean up stale generations on app load
        if (state) {
          state.clearStaleGenerations();
        }
      },
    }
  )
);
